import { z } from "zod";
import { MAX_PROVIDER_ROSTER_IMPORT_ROWS } from "@api/shared-runtime/provider-roster-import";

const CSV_HEADERS = [
  "email",
  "first_name",
  "last_name",
  "phone",
  "npi",
  "professional_type",
  "specialty_codes",
  "send_invitation",
] as const;

type CsvHeader = (typeof CSV_HEADERS)[number];

export const PROVIDER_CSV_ACCEPT = ".csv,text/csv";
export const MAX_PROVIDER_CSV_FILE_SIZE_BYTES = 1_000_000;
export const PROVIDER_CSV_TEMPLATE_FILE_NAME = "provider-roster-example.csv";

export type ProviderCsvValues = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  npi?: string;
  healthcareProfessionalTypeId?: string;
  professionalTypeName?: string;
  specialtyCodes?: string[];
  sendInvitation: boolean;
};

export type ParsedProviderCsvRow = {
  rowNumber: number;
  values: ProviderCsvValues;
  errors: string[];
};

export class ProviderCsvError extends Error {}

const isBlankRecord = (record: string[]) =>
  record.every((cell) => cell.trim() === "");

/** Parse RFC 4180-style commas, escaped quotes and quoted newlines locally. */
const parseCsvRecords = (text: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let insideQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (insideQuotes) {
      if (character === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        insideQuotes = false;
      } else {
        field += character;
      }
      continue;
    }

    if (character === '"' && field === "") {
      insideQuotes = true;
    } else if (character === ",") {
      record.push(field);
      field = "";
    } else if (character === "\n") {
      record.push(field);
      if (!isBlankRecord(record)) {
        records.push(record);
      }
      record = [];
      field = "";
    } else if (character !== "\r") {
      field += character;
    }
  }

  if (insideQuotes) {
    throw new ProviderCsvError("The CSV contains an unclosed quoted value.");
  }
  record.push(field);
  if (!isBlankRecord(record)) {
    records.push(record);
  }
  return records;
};

const normalizeHeader = (header: string) =>
  header
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[ -]+/g, "_");

const parseSendInvitation = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase();
  if (["", "false", "no", "0"].includes(normalized)) {
    return false;
  }
  if (["true", "yes", "1"].includes(normalized)) {
    return true;
  }
  return null;
};

const getCell = ({
  record,
  headerIndexes,
  header,
}: {
  record: string[];
  headerIndexes: Map<CsvHeader, number>;
  header: CsvHeader;
}) => {
  const index = headerIndexes.get(header);
  return index === undefined ? "" : (record[index] ?? "").trim();
};

const parseProviderRecord = ({
  record,
  rowNumber,
  headerIndexes,
  professionalTypes,
}: {
  record: string[];
  rowNumber: number;
  headerIndexes: Map<CsvHeader, number>;
  professionalTypes: { id: string; name: string }[];
}): ParsedProviderCsvRow => {
  const valueFor = (header: CsvHeader) =>
    getCell({ record, headerIndexes, header });
  const email = valueFor("email");
  const firstName = valueFor("first_name");
  const lastName = valueFor("last_name");
  const phone = valueFor("phone");
  const npi = valueFor("npi");
  const professionalTypeName = valueFor("professional_type");
  const specialtyCodes = valueFor("specialty_codes")
    .split(";")
    .map((code) => code.trim())
    .filter(Boolean);
  const sendInvitationValue = valueFor("send_invitation");
  const sendInvitation = parseSendInvitation(sendInvitationValue);
  const professionalType = professionalTypes.find(
    (type) =>
      type.name.trim().toLowerCase() === professionalTypeName.toLowerCase(),
  );
  const errors: string[] = [];

  if (email && !z.string().email().safeParse(email).success) {
    errors.push("Invalid email address");
  }
  if (npi && !/^\d{10}$/.test(npi)) {
    errors.push("NPI must be exactly 10 digits");
  }
  if (firstName.length > 200 || lastName.length > 200) {
    errors.push("Names must be 200 characters or fewer");
  }
  if (phone.length > 50) {
    errors.push("Phone must be 50 characters or fewer");
  }
  if (professionalTypeName && !professionalType) {
    errors.push(`Unknown professional type: ${professionalTypeName}`);
  }
  if (sendInvitation === null) {
    errors.push("Send invitation must be true or false");
  }
  if (sendInvitation === true && !email) {
    errors.push("Email is required to send an invitation");
  }
  if (!email && !npi && !firstName && !lastName) {
    errors.push("Enter a provider name, NPI, or email");
  }

  return {
    rowNumber,
    values: {
      email: email || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      phone: phone || undefined,
      npi: npi || undefined,
      healthcareProfessionalTypeId: professionalType?.id,
      professionalTypeName: professionalTypeName || undefined,
      specialtyCodes,
      sendInvitation: sendInvitation ?? false,
    },
    errors,
  };
};

/** Flags a row that repeats an email or NPI from an earlier row of the file. */
const addDuplicateErrors = (rows: ParsedProviderCsvRow[]) => {
  const firstRowByIdentifier = new Map<string, number>();
  for (const row of rows) {
    const identifiers = [
      row.values.email?.trim().toLowerCase(),
      row.values.npi?.trim(),
    ].filter((identifier): identifier is string => Boolean(identifier));
    const earlierRowNumber = identifiers
      .map((identifier) => firstRowByIdentifier.get(identifier))
      .find((rowNumber) => rowNumber !== undefined);
    if (earlierRowNumber !== undefined) {
      row.errors.push(`Duplicate of row ${earlierRowNumber}`);
      continue;
    }
    identifiers.forEach((identifier) =>
      firstRowByIdentifier.set(identifier, row.rowNumber),
    );
  }
};

export const parseProviderCsv = ({
  text,
  professionalTypes,
}: {
  text: string;
  professionalTypes: { id: string; name: string }[];
}): ParsedProviderCsvRow[] => {
  const records = parseCsvRecords(text);
  if (records.length < 2) {
    throw new ProviderCsvError(
      "The CSV must contain a header and at least one provider.",
    );
  }

  const normalizedHeaders = records[0].map(normalizeHeader);
  const unknownHeaders = normalizedHeaders.filter(
    (header) => !CSV_HEADERS.includes(header as CsvHeader),
  );
  if (unknownHeaders.length > 0) {
    throw new ProviderCsvError(
      `Unknown CSV column${unknownHeaders.length === 1 ? "" : "s"}: ${unknownHeaders.join(", ")}`,
    );
  }
  if (new Set(normalizedHeaders).size !== normalizedHeaders.length) {
    throw new ProviderCsvError("The CSV contains duplicate columns.");
  }

  const headerIndexes = new Map<CsvHeader, number>();
  normalizedHeaders.forEach((header, index) => {
    headerIndexes.set(header as CsvHeader, index);
  });
  const providerRecords = records.slice(1);
  if (providerRecords.length > MAX_PROVIDER_ROSTER_IMPORT_ROWS) {
    throw new ProviderCsvError(
      `A CSV can contain at most ${MAX_PROVIDER_ROSTER_IMPORT_ROWS} providers.`,
    );
  }

  const rows = providerRecords.map((record, index) => {
    const row = parseProviderRecord({
      record,
      rowNumber: index + 2,
      headerIndexes,
      professionalTypes,
    });
    if (record.length !== normalizedHeaders.length) {
      row.errors.push("Column count does not match the header");
    }
    return row;
  });
  addDuplicateErrors(rows);
  return rows;
};

export const downloadProviderCsvTemplate = () => {
  const csv = [
    CSV_HEADERS.join(","),
    "jane.doe@example.com,Jane,Doe,5551234567,1234567890,,,false",
  ].join("\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = PROVIDER_CSV_TEMPLATE_FILE_NAME;
  link.click();
  URL.revokeObjectURL(url);
};
